import { FormRenderProps } from "@progress/kendo-react-form";
import { useState, useEffect } from "react";
import { Company } from "../Company";
import { ConfigurationActions, ConfigurationCompaniesSelector } from ".";
import { Configuration } from "./Configuration";
import { ConfigAction } from "./ConfigurationEnums";

interface ICompanySelectionStepProps {
  formRenderProps: FormRenderProps;
  companies: Company[];
  companyId: number;
  configurations: Record<number, Configuration[]>;
  margins: any[];
  marginField: string;
  showAction?: boolean;
  selectMultiple?: boolean;
}

export const CompanySelectionStep = ({
  formRenderProps,
  companies,
  companyId,
  configurations,
  margins,
  marginField,
  showAction,
  selectMultiple,
}: ICompanySelectionStepProps) => {
  const businessTypes: number[] = selectMultiple
    ? formRenderProps.valueGetter("businessTypes")
    : undefined;

  const descendentCompanies = (companies ?? [])
    .filter((c) => !businessTypes || businessTypes.every((t) => c.businessTypes.includes(t)))
    .filter((c) => c.id !== companyId);

  const childCompanies = descendentCompanies.filter((c) => c.parentCompanyId === companyId);

  const selectedCompanies: number[] = formRenderProps.valueGetter("companies") || [];
  const selectedCompany = formRenderProps.valueGetter("company");

  if (
    !selectMultiple &&
    selectedCompany &&
    selectedCompanies.join(",") !== selectedCompany.value.toString()
  ) {
    formRenderProps.onChange("company", { value: null });
    formRenderProps.onChange("companies", { value: [] });
  } else {
    const ids = childCompanies.map((cc) => cc.id);
    const filtered = selectedCompanies.filter((id) => ids.includes(id));
    if (filtered.length !== selectedCompanies.length)
      formRenderProps.onChange("companies", { value: filtered });
  }

  const [isAssign, setIsAssign] = useState(
    formRenderProps.valueGetter("action") === ConfigAction.Assign,
  );

  useEffect(() => {
    if (!isAssign) formRenderProps.onChange("isActive", { value: false });
  }, [isAssign]);

  const onChangeCompany = (value: number) =>
    formRenderProps.onChange("companies", { value: [value] });

  const onCopySelections = (copyCompanyId: number) => {
    // Copy assignments from another company
    const copyCompanyConfigs = configurations[copyCompanyId] ?? [];
    const productIds = copyCompanyConfigs.map((c) => c.productId);
    const parentConfigs = (configurations[companyId] ?? []).filter((c) =>
      productIds.includes(c.productId),
    );
    const configIds = parentConfigs.map((c) => c.configurationId);

    formRenderProps.onChange("configurations", { value: configIds });

    parentConfigs.forEach((c) => {
      const config: any = copyCompanyConfigs.find((cc) => c.productId === cc.productId);
      const margin = margins.find((m) => m.marginId === config[marginField]);

      if (margin)
        formRenderProps.onChange(`marginSelection_${c.productId}`, {
          value: { value: margin.marginId, label: margin.longName },
        });
    });
  };

  return (
    <div className="stepper-form">
      {showAction && (
        <ConfigurationActions
          onChange={(value: string) => setIsAssign(value === ConfigAction.Assign)}
        />
      )}

      <ConfigurationCompaniesSelector
        childCompanies={childCompanies}
        descendentCompanies={descendentCompanies}
        multiSelect={selectMultiple}
        isAssign={isAssign}
        onChange={!selectMultiple ? onChangeCompany : undefined}
        onCopy={!selectMultiple ? onCopySelections : undefined}
      />
    </div>
  );
};
